import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api';
import AdsDisplay from '../components/AdsDisplay';
import { DollarSign, CheckCircle, AlertCircle, Info, Check, Lock } from 'react-feather';
import { getAvailableCurrencies, getCurrencyInfo } from '../utils/currency';
import { SWYCHR_MARKETS } from '../utils/swychrMarkets';
import './Auth.css';

const CURRENCIES = getAvailableCurrencies();

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [currency, setCurrency] = useState(user?.currency || 'USD');
  const [countryCode, setCountryCode] = useState(user?.country_code || '');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [success, setSuccess] = useState(false);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  useEffect(() => {
    if (user?.currency) {
      setCurrency(getCurrencyInfo(user.currency) ? user.currency : 'USD');
    }
    setCountryCode(user?.country_code || '');
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg('');
    setSuccess(false);

    try {
      await authAPI.updateProfile({ currency, country_code: countryCode || null });
      setSuccess(true);
      setMsg('Country and currency updated successfully!');
      await refreshUser();
    } catch (err) {
      setSuccess(false);
      setMsg(err.response?.data?.error || 'Failed to update currency');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMsg('');
    setPasswordSuccess(false);

    // Validation
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordMsg('All fields are required');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMsg('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordMsg('New password must be at least 6 characters');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.oldPassword === passwordForm.newPassword) {
      setPasswordMsg('New password must be different from current password');
      setPasswordLoading(false);
      return;
    }

    try {
      await authAPI.changePassword({
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordSuccess(true);
      setPasswordMsg('Password changed successfully!');
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordSuccess(false);
      setPasswordMsg(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const currentCurrencyObj = getCurrencyInfo(currency);

  return (
    <div>
      <h1 className="page-title">Settings</h1>
      <p className="page-subtitle">Manage your account preferences</p>

      {/* Currency Section */}
      <form onSubmit={handleSave} style={{ maxWidth: 560, marginBottom: 24 }}>
        {msg && (
          <div className={`alert ${success ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              {success ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              <span>{msg}</span>
            </div>
          </div>
        )}

        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
            <DollarSign size={20} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <h3 style={{ fontSize: 16, margin: 0 }}>Account Currency</h3>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
            Choose your country and preferred currency for deposits, prices, and balance display.
          </p>

          <div className="form-group">
            <label className="form-label">Country</label>
            <select
              className="form-input"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              disabled={loading}
              style={{
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <option value="">Select your country</option>
              {SWYCHR_MARKETS.map((market) => (
                <option key={market.countryCode} value={market.countryCode}>
                  {market.label}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              Swychr deposits will default to this country when it is supported.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Currency</label>
            <select
              className="form-input"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={loading}
              style={{
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              {CURRENCIES.map((curr) => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol} {curr.code} - {curr.name}
                </option>
              ))}
            </select>
            {currentCurrencyObj && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                Currently selected: <strong>{currentCurrencyObj.symbol} {currentCurrencyObj.code}</strong> - {currentCurrencyObj.name}
              </div>
            )}
          </div>

          <button
            className="btn btn-primary btn-lg"
            type="submit"
            disabled={loading || (currency === user?.currency && countryCode === (user?.country_code || ''))}
            style={{
              opacity: loading || (currency === user?.currency && countryCode === (user?.country_code || '')) ? 0.6 : 1,
              cursor: loading || (currency === user?.currency && countryCode === (user?.country_code || '')) ? 'not-allowed' : 'pointer',
              background: '#1e40af',
            }}
          >
            {loading ? 'Updating...' : <><Check size={16} style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} /> Save Preferences</>}
          </button>
        </div>
      </form>

      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />

      <AdsDisplay adSlot="banner-320x50" className="banner" />

      {/* Password Change Section */}
      <form onSubmit={handlePasswordChange} style={{ maxWidth: 560 }}>
        {passwordMsg && (
          <div className={`alert ${passwordSuccess ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              {passwordSuccess ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              <span>{passwordMsg}</span>
            </div>
          </div>
        )}

        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
            <Lock size={20} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <h3 style={{ fontSize: 16, margin: 0 }}>Change Password</h3>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
            Update your password to keep your account secure.
          </p>

          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.oldPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={passwordLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              placeholder="••••••••"
              autoComplete="new-password"
              disabled={passwordLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              placeholder="••••••••"
              autoComplete="new-password"
              disabled={passwordLoading}
            />
          </div>

          <button
            className="btn btn-primary btn-lg"
            type="submit"
            disabled={passwordLoading || !passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
            style={{
              opacity: passwordLoading || !passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword ? 0.6 : 1,
              cursor: passwordLoading || !passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword ? 'not-allowed' : 'pointer',
              background: '#1e40af',
            }}
          >
            {passwordLoading ? 'Updating...' : <><Check size={16} style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} /> Change Password</>}
          </button>
        </div>

        <div className="card" style={{ marginBottom: 20, background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Info size={18} style={{ marginTop: 2, color: 'var(--accent)', flexShrink: 0 }} />
            <div>
              <h3 style={{ marginBottom: 12, fontSize: 14, margin: '0 0 8px 0' }}>Security Tips</h3>
              <ul style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, paddingLeft: 20 }}>
                <li>Use a strong password with uppercase, lowercase, numbers, and symbols</li>
                <li>Never share your password with anyone</li>
                <li>Change your password regularly for better security</li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
